import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown, ChevronRight, Phone, Calendar } from 'lucide-react';
import { Button } from '../ui/button';
import { navigation, kontaktDaten } from '../../data/mock';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const pathname = location.pathname;

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
    setOpenSubmenu(null);
  }, [pathname]);

  return (
    <header
      className={`header-nav transition-all duration-300 ${
        isScrolled
          ? 'bg-[#032925]/95 backdrop-blur-md'
          : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto flex items-center justify-between h-full px-4 lg:px-8">
        {/* Logo mit starkem Schatten */}
        <Link
          to="/"
          className="flex items-center gap-3"
          style={{
            filter:
              'drop-shadow(0 1px 2px rgba(0,0,0,0.9)) drop-shadow(0 2px 6px rgba(0,0,0,0.7))'
          }}
        >
          <img
            src="/logo-gelb.png"
            alt="Medicum Diez"
            className="h-14 w-auto"
          />
        </Link>

        {/* Desktop Navigation */}
        <nav
          className="hidden lg:flex items-center gap-1"
          style={{
            textShadow:
              '0 1px 2px rgba(0,0,0,0.9), 0 2px 6px rgba(0,0,0,0.7)'
          }}
        >
          {navigation.map((item) => (
            <div key={item.id} className="relative">
              {item.children ? (
                <div
                  className="relative"
                  onMouseEnter={() => setOpenDropdown(item.id)}
                  onMouseLeave={() => {
                    setOpenDropdown(null);
                    setOpenSubmenu(null);
                  }}
                >
                  <Link
                    to={item.path}
                    className={`flex items-center gap-1 px-4 py-2 text-sm font-medium transition-colors rounded-full ${
                      location.pathname.startsWith(item.path)
                        ? 'text-[#edd68d] bg-white/10'
                        : 'text-white/90 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    {item.label}

                    <ChevronDown
                      className={`w-4 h-4 transition-transform ${
                        openDropdown === item.id ? 'rotate-180' : ''
                      }`}
                    />
                  </Link>

                  <AnimatePresence>
                    {openDropdown === item.id && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.2 }}
                        className="absolute top-full left-0 mt-1 w-80 bg-[#f9f3e2] rounded-xl shadow-xl border border-[#032925]/10 py-2 z-50"
                      >
                        {item.children.map((child) => (
                          <div
                            key={child.id}
                            className="relative"
                            onMouseEnter={() =>
                              child.children && setOpenSubmenu(child.id)
                            }
                            onMouseLeave={() => setOpenSubmenu(null)}
                          >
                            {child.external ? (
                              <a
                                href={child.path}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-between px-4 py-2.5 text-sm font-semibold text-[#423115]/80 hover:text-[#423115] hover:bg-[#8ae9bb]/30 transition-colors [text-shadow:none]"
                              >
                                {child.label}
                              </a>
                            ) : (
                              <Link
                                to={child.path}
                                className="flex items-center justify-between px-4 py-2.5 text-sm font-semibold text-[#423115]/80 hover:text-[#423115] hover:bg-[#8ae9bb]/30 transition-colors [text-shadow:none]"
                              >
                                {child.label}

                                {child.children && (
                                  <ChevronRight className="w-4 h-4" />
                                )}
                              </Link>
                            )}

                            {/* Untermenü der zweiten Ebene */}
                            <AnimatePresence>
                              {openSubmenu === child.id &&
                                child.children && (
                                  <motion.div
                                    initial={{ opacity: 0, x: 10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: 10 }}
                                    transition={{ duration: 0.2 }}
                                    className="absolute left-full top-0 ml-1 w-72 bg-[#f9f3e2] rounded-xl shadow-xl border border-[#032925]/10 py-2 z-50"
                                  >
                                    {child.children.map((subItem) => (
                                      <Link
                                        key={subItem.id}
                                        to={subItem.path}
                                        className="block px-4 py-2 text-sm font-semibold text-[#423115]/80 hover:text-[#423115] hover:bg-[#8ae9bb]/30 transition-colors [text-shadow:none]"
                                      >
                                        {subItem.label}
                                      </Link>
                                    ))}
                                  </motion.div>
                                )}
                            </AnimatePresence>
                          </div>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <Link
                  to={item.path}
                  className={`px-4 py-2 text-sm font-medium transition-colors rounded-full ${
                    location.pathname === item.path
                      ? 'text-[#edd68d] bg-white/10'
                      : 'text-white/90 hover:text-white hover:bg-white/10'
                  }`}
                >
                  {item.label}
                </Link>
              )}
            </div>
          ))}
        </nav>

        {/* CTA Button nur auf Desktop */}
        <div
          className="hidden lg:flex items-center gap-3"
          style={{
            filter:
              'drop-shadow(0 1px 2px rgba(0,0,0,0.9)) drop-shadow(0 2px 6px rgba(0,0,0,0.7))'
          }}
        >
          <Link to="/termin">
            <Button className="bg-[#edd68d] hover:bg-[#d4c17d] text-[#032925] font-semibold px-6 rounded-full">
              <Calendar className="w-4 h-4 mr-2" />
              Termin buchen
            </Button>
          </Link>
        </div>

        {/* Mobile Burger-Menü */}
        <button
          className="lg:hidden p-2 text-white"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          style={{
            filter:
              'drop-shadow(0 1px 2px rgba(0,0,0,0.9)) drop-shadow(0 2px 6px rgba(0,0,0,0.7))'
          }}
        >
          {isMobileMenuOpen ? (
            <X className="w-6 h-6" />
          ) : (
            <Menu className="w-6 h-6" />
          )}
        </button>
      </div>

      {/* Mobile Menü */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="lg:hidden fixed inset-0 top-[80px] bg-[#032925] z-[9000] overflow-y-auto"
            style={{ paddingBottom: 80 }}
          >
            <nav className="container mx-auto py-4 px-4">
              {navigation.map((item) => (
                <div
                  key={item.id}
                  className="py-2 border-b border-[#edd68d]/10"
                >
                  <Link
                    to={item.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block py-2 text-[#edd68d] font-medium text-lg"
                  >
                    {item.label}
                  </Link>

                  {item.children && (
                    <div className="pl-4 mt-1 space-y-1 pb-2">
                      {item.children.map((child) =>
                        child.external ? (
                          <a
                            key={child.id}
                            href={child.path}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => setIsMobileMenuOpen(false)}
                            className="block py-1.5 text-sm text-[#edd68d]/70 hover:text-[#edd68d]"
                          >
                            {child.label}
                          </a>
                        ) : (
                          <Link
                            key={child.id}
                            to={child.path}
                            onClick={() => setIsMobileMenuOpen(false)}
                            className="block py-1.5 text-sm text-[#edd68d]/70 hover:text-[#edd68d]"
                          >
                            {child.label}
                          </Link>
                        )
                      )}
                    </div>
                  )}
                </div>
              ))}

              <Link
                to="/termin"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block mt-6 mb-8"
              >
                <Button className="w-full bg-[#edd68d] hover:bg-[#d4c17d] text-[#032925] font-semibold rounded-full py-6">
                  <Calendar className="w-5 h-5 mr-2" />
                  Termin buchen
                </Button>
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
